import { useState } from "react";
import { StatusBadge } from "@/components/shared";
import { CalendarClock, Check, X, MapPin, CalendarPlus, Clock } from "lucide-react";

const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };
const fmtTime = (t) => { if (!t) return ""; const [h, m] = t.split(":"); const H = Number(h); const ap = H >= 12 ? "PM" : "AM"; const h12 = ((H + 11) % 12) + 1; return `${h12}:${m} ${ap}`; };
const fmtDT = (d, t) => `${fmtDate(d)} at ${fmtTime(t)}`;

export const ShowingCard = ({ showing: s, role, respond }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(s.proposed_date || s.requested_date || "");
  const [time, setTime] = useState(s.proposed_time || s.requested_time || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const awaitingLandlord = ["awaiting_landlord", "requested", "pending"].includes(s.status);
  const myTurn = (role === "landlord" && awaitingLandlord) || (role === "renter" && s.status === "awaiting_renter");
  const other = role === "landlord" ? (s.renter_name || "the renter") : (s.landlord_name || "the landlord");
  const pDate = s.proposed_date || s.requested_date;
  const pTime = s.proposed_time || s.requested_time;
  const proposedByMe = s.proposed_by === role;

  const act = async (action, extra = {}) => {
    setBusy(true);
    const ok = await respond(s.id, { action, note: note.trim(), ...extra });
    setBusy(false);
    if (ok) { setOpen(false); setNote(""); }
  };

  return (
    <div data-testid={`showing-card-${s.id}`} className="bg-white border border-line rounded-xl p-5">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-medium text-ink">{s.property_title}</p>
        <StatusBadge status={s.status} testid={`showing-status-${s.id}`} />
        {role === "landlord" && <span className="text-sm text-mute">· {s.renter_name}</span>}
      </div>

      {s.status === "confirmed" ? (
        <div className="mt-2 rounded-lg bg-forest/[0.05] border border-forest/15 px-3 py-2.5">
          <p className="text-sm font-medium text-forest flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Confirmed for {fmtDT(s.confirmed_date, s.confirmed_time)}</p>
          {s.property_address && <p className="text-xs text-mute mt-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {s.property_address}</p>}
          <p className="text-[11px] text-mute mt-1">We'll send a reminder (text if available, email & in-app) about 1 hour before.</p>
        </div>
      ) : (
        <p className="text-sm text-mute mt-1 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {["declined", "completed"].includes(s.status) ? fmtDT(pDate, pTime) : <>Proposed: <span className="text-ink font-medium">{fmtDT(pDate, pTime)}</span> · by {proposedByMe ? "you" : (s.proposed_by === "landlord" ? (role === "renter" ? "the landlord" : "you") : (s.renter_name || "the renter"))}</>}
        </p>
      )}
      {s.note && <p className="text-sm text-mute mt-1 italic">"{s.note}"</p>}

      {/* Action zone */}
      {myTurn && (
        <div className="mt-3">
          {!open ? (
            <div className="flex gap-2 flex-wrap">
              <button data-testid={`showing-confirm-${s.id}`} disabled={busy} onClick={() => act("confirm")} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover flex items-center gap-1.5 disabled:opacity-60"><Check className="h-4 w-4" /> Confirm {fmtDT(pDate, pTime)}</button>
              <button data-testid={`showing-propose-toggle-${s.id}`} onClick={() => setOpen(true)} className="border border-line text-ink px-3 py-1.5 rounded-lg text-sm hover:bg-bone flex items-center gap-1.5"><CalendarPlus className="h-4 w-4 text-forest" /> Propose new time</button>
              <button data-testid={`showing-decline-${s.id}`} disabled={busy} onClick={() => act("decline")} className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50">Decline</button>
            </div>
          ) : (
            <div className="space-y-2 rounded-lg border border-line p-3">
              <p className="text-xs font-medium text-ink">Suggest a different date & time</p>
              <div className="flex gap-2 flex-wrap">
                <input data-testid={`showing-propose-date-${s.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
                <input data-testid={`showing-propose-time-${s.id}`} type="time" value={time} onChange={(e) => setTime(e.target.value)} className="border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
              </div>
              <input data-testid={`showing-propose-note-${s.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
              <div className="flex gap-2">
                <button data-testid={`showing-propose-send-${s.id}`} disabled={busy || !date || !time} onClick={() => act("propose", { date, time })} className="bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60">Send proposal</button>
                <button onClick={() => setOpen(false)} className="border border-line px-3 py-2 rounded-lg text-sm hover:bg-bone">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {!myTurn && ["awaiting_landlord", "awaiting_renter", "requested", "pending"].includes(s.status) && (
        <p data-testid={`showing-waiting-${s.id}`} className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Waiting for {other} to confirm the proposed time or suggest another.</p>
      )}

      {s.status === "confirmed" && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {role === "landlord" && <button data-testid={`showing-complete-${s.id}`} disabled={busy} onClick={() => act("complete")} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover">Mark completed</button>}
          <button data-testid={`showing-cancel-${s.id}`} disabled={busy} onClick={() => act("decline")} className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50 flex items-center gap-1.5"><X className="h-3.5 w-3.5" /> Cancel showing</button>
        </div>
      )}

      {(s.history || []).length > 1 && (
        <details className="mt-3">
          <summary className="text-xs text-mute cursor-pointer hover:text-ink">Scheduling history ({s.history.length})</summary>
          <ol className="mt-2 space-y-1.5 border-l border-line pl-3">
            {s.history.map((h) => (
              <li key={`${h.by}-${h.action}-${h.date || ""}-${h.time || ""}`} className="text-xs text-mute">
                <span className="font-medium text-ink capitalize">{h.by}</span> {h.action}{h.date ? ` · ${fmtDT(h.date, h.time)}` : ""}{h.note ? ` — "${h.note}"` : ""}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
};
